import { COUNTRIES } from "@/src/constants/countries";
import { UserRole } from "@/src/models/user.model";
import { capitalizeName } from "@/src/utils/string.utils";
import { downloadRowsAsExcel } from "@/src/utils/export-excel.utils";
import * as DocumentPicker from "expo-document-picker";
import * as XLSX from "xlsx";

const normalizeRole = (role?: string): UserRole =>
  role?.toLowerCase() === "admin" ? UserRole.ADMIN : UserRole.MEMBER;

/**
 * Header keys are compared with spaces, underscores and hyphens stripped and
 * lowercased, so "Business Unit", "business_unit" and "business-unit" all match
 * the same alias. Exported for tests, which pin the headers the real export ships.
 */
export const normalizeHeaderKey = (key: string) =>
  key.toLowerCase().replace(/[\s_-]/g, "");

/** Header aliases, so a sheet saying "First Name" or "E-mail" still imports. */
const FIRST_NAME_KEYS = ["firstname", "givenname", "first"];
const LAST_NAME_KEYS = ["lastname", "surname", "familyname", "last"];
// `userprincipalname` is what an Entra/AD export calls the email column, and it is
// the header on the sheets this is actually used with. Without it the email column
// is not found and every row fails validation — the import then reports "no rows
// had a valid email", which reads like bad data rather than an unrecognised header.
const EMAIL_KEYS = [
  "email",
  "emailaddress",
  "mail",
  "userprincipalname",
  "upn",
];
const COUNTRY_KEYS = ["country", "countryname", "nation", "nationality"];
// The sheet calls it "Business Unit"; the profile field is `department`. Stored as
// free text — unlike country, nothing filters on it, so there is no canonical list
// to reconcile against.
const DEPARTMENT_KEYS = [
  "businessunit",
  "department",
  "businessunitname",
  "bu",
];
// The sheet calls it "Title"; the profile field is `designation`. Free text for the
// same reason as department — nothing filters on it.
// Deliberately NOT "role": that column carries the app role (admin/member) and is
// already consumed by normalizeRole. Treating it as a job title would put "admin"
// in someone's designation.
const DESIGNATION_KEYS = ["title", "designation", "jobtitle", "position"];

/**
 * A row the sheet actually yields. Not `User` — these people do not exist yet, so
 * there is no itemId. `country`, `department` and `designation` ride along so the
 * invite can seed a profile with them.
 *
 * `firstName`, `lastName` and `email` are all required — a row missing any of
 * them is dropped before this type is ever constructed (see the `valid`
 * filter below), so a row that reaches this type always has all three.
 */
export type ImportedUserRow = Readonly<{
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  country: string | null;
  department: string | null;
  designation: string | null;
}>;

/**
 * Maps a sheet's country text to the app's canonical country name.
 *
 * The invite modal filters with an exact, case-sensitive match against
 * `COUNTRIES`, so a value that merely looks right ("bangladesh", "BANGLADESH",
 * " Bhutan") would store fine and then never match a filter. Comparing
 * case-insensitively and returning the canonical spelling keeps the stored data
 * filterable.
 *
 * Returns null for blank input, and undefined for a value that matches nothing —
 * the caller distinguishes "no country given" from "country not recognised".
 */
export const canonicalCountry = (
  raw: string,
): string | null | undefined => {
  const value = raw.trim();
  if (!value) return null;

  const match = COUNTRIES.find(
    (country) => country.name.toLowerCase() === value.toLowerCase(),
  );
  return match?.name;
};

const pick = (row: Record<string, any>, keys: string[]): string => {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim())
      return String(value).trim();
  }
  return "";
};

/**
 * Read the picked file's bytes.
 *
 * On web, expo-document-picker hands back a `File` alongside a `uri` that is a
 * blob:/data: URL. Prefer the `File`: reading it directly avoids a `fetch()` on
 * that URL, which is the fragile step (a strict CSP can block `data:`/`blob:`
 * fetches outright, and the failure surfaces as an opaque network error).
 *
 * `XLSX.read` is given a `Uint8Array` rather than the raw `ArrayBuffer` — both
 * happen to work with `type: "array"`, but the typed array is what SheetJS
 * documents.
 */
const readFileBytes = async (
  file: DocumentPicker.DocumentPickerAsset,
): Promise<Uint8Array> => {
  const webFile = (file as { file?: File }).file;
  if (webFile && typeof webFile.arrayBuffer === "function") {
    return new Uint8Array(await webFile.arrayBuffer());
  }

  const response = await fetch(file.uri);
  if (!response.ok) {
    throw new Error(`Could not read the selected file (${response.status})`);
  }
  return new Uint8Array(await response.arrayBuffer());
};

const isValidEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * A row that did NOT make it into the invite list, alongside why. `raw` is the
 * row exactly as `sheet_to_json` read it — original headers, original casing,
 * untouched — so it can be written straight back out into a file the admin can
 * fix and re-upload without having to reconcile column names by hand.
 */
export type DroppedUserRow = Readonly<{
  raw: Record<string, any>;
  reason: string;
}>;

export type ImportExcelResult = Readonly<{
  imported: ImportedUserRow[];
  dropped: DroppedUserRow[];
  /** The uploaded file's name, so downstream downloads can be named after it. */
  sourceFileName: string;
}>;

/** Headers the parser recognises, in the order the downloadable template uses. */
export const TEMPLATE_HEADERS = [
  "First Name",
  "Last Name",
  "Email",
  "Role",
  "Country",
  "Business Unit",
  "Title",
] as const;

const TEMPLATE_EXAMPLE_ROW = {
  "First Name": "Jane",
  "Last Name": "Doe",
  Email: "jane.doe@example.com",
  Role: "member",
  Country: "Switzerland",
  "Business Unit": "Engineering",
  Title: "Software Engineer",
};

/** A blank sheet with the headers this parser understands, for admins building a fresh list. */
export const downloadImportTemplate = async (): Promise<void> => {
  await downloadRowsAsExcel(
    [TEMPLATE_EXAMPLE_ROW],
    "user_import_template.xlsx",
    "Users",
  );
};

/**
 * Re-exports everyone who did NOT end up invited — both rows dropped before
 * sending (bad email, missing name, in-sheet duplicate — full original
 * columns, since those never left the browser) and rows the server rejected
 * once actually invited (e.g. "Email already exists" — email + reason only,
 * since that is all a failed invite carries). One combined file, named after
 * the source sheet where there was one (e.g. `invites.xlsx` →
 * `invites_failed.xlsx`), so there is a single list to fix and re-upload.
 */
export const downloadFailedRows = async (
  sourceFileName: string,
  rows: DroppedUserRow[],
): Promise<void> => {
  const sheetRows = rows.map(({ raw, reason }) => ({ ...raw, Reason: reason }));
  const base = sourceFileName.replace(/\.[^./]+$/, "") || "invite";
  await downloadRowsAsExcel(sheetRows, `${base}_failed.xlsx`, "Failed");
};

export const importInviteUsersFromExcel = async (
  file?: DocumentPicker.DocumentPickerAsset,
): Promise<ImportExcelResult> => {
  const empty: ImportExcelResult = { imported: [], dropped: [], sourceFileName: "" };

  let selectedFile = file;

  if (!selectedFile) {
    const result = await DocumentPicker.getDocumentAsync({
      type: [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        ".xlsx",
        ".xls",
      ],
      copyToCacheDirectory: true,
    });

    if (result.canceled || !result.assets || result.assets.length === 0) {
      return empty;
    }

    selectedFile = result.assets[0];
  }

  const sourceFileName = selectedFile.name ?? "import.xlsx";
  const bytes = await readFileBytes(selectedFile);
  const workbook = XLSX.read(bytes, { type: "array" });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return { ...empty, sourceFileName };

  const sheet = workbook.Sheets[sheetName];
  const rawRows = XLSX.utils.sheet_to_json<any>(sheet);

  if (!rawRows.length) {
    throw new Error("The first sheet has no data rows.");
  }

  const unrecognisedCountries = new Set<string>();
  const imported: ImportedUserRow[] = [];
  const dropped: DroppedUserRow[] = [];
  const seenEmails = new Set<string>();

  rawRows.forEach((raw) => {
    const row: Record<string, any> = {};
    Object.keys(raw).forEach((key) => {
      row[normalizeHeaderKey(key)] = raw[key];
    });

    const rawCountry = pick(row, COUNTRY_KEYS);
    const country = canonicalCountry(rawCountry);
    // Track anything the sheet offered that we could not map, so it can be
    // reported rather than quietly imported as "no country".
    if (country === undefined) unrecognisedCountries.add(rawCountry);

    const email = pick(row, EMAIL_KEYS);
    const candidate = {
      // Capitalized on the way in, not just on the way out. These names are
      // written to the IAM account and the profile row, so whatever lands here
      // is what every other consumer reads — including ones that do no
      // formatting of their own. Directory exports routinely carry all-lower
      // ("john doe") or all-upper ("JOHN DOE") cells; the former looked wrong
      // everywhere, and normalizing at the single point of entry fixes it for
      // every reader at once.
      //
      // `capitalizeName` only touches each word's initial, so "JOHN DOE" is
      // left as the sheet had it rather than being rewritten to "John Doe" —
      // an all-caps cell may be a genuine spelling, and guessing costs more
      // than it gains. It also means "mcDonald" survives as "McDonald".
      firstName: capitalizeName(pick(row, FIRST_NAME_KEYS)),
      lastName: capitalizeName(pick(row, LAST_NAME_KEYS)),
      email,
      role: normalizeRole(pick(row, ["role"]) || undefined),
      // null when the sheet gave nothing, and also when it gave something
      // unmappable — an unmatched string would be unfilterable anyway.
      country: country ?? null,
      department: pick(row, DEPARTMENT_KEYS) || null,
      designation: pick(row, DESIGNATION_KEYS) || null,
    };

    // Email, first name and last name are all required — matches the
    // single-invite form, which requires all three for the same reason: the
    // account is created active with no later onboarding step to fill a
    // missing name in.
    let reason: string | null = null;
    if (!email) reason = "Missing email";
    else if (!isValidEmail(email)) reason = "Invalid email address";
    else if (!candidate.firstName) reason = "Missing first name";
    else if (!candidate.lastName) reason = "Missing last name";
    // The invite mutation has no dedup of its own — a repeated email would
    // otherwise burn a concurrency slot on a guaranteed "Email already
    // exists" failure (see useInviteUsers). Keep the first occurrence per
    // email (case-insensitive, since Blocks treats the address that way)
    // and drop the rest here instead.
    else if (seenEmails.has(email.toLowerCase()))
      reason = "Duplicate email — already imported from an earlier row";

    if (reason) {
      dropped.push({ raw, reason });
      return;
    }

    seenEmails.add(email.toLowerCase());
    imported.push(candidate as ImportedUserRow);
  });

  if (unrecognisedCountries.size) {
    console.warn(
      "Excel import: these country values did not match a known country and " +
        "were imported without one. Fix the sheet and re-import those rows:",
      [...unrecognisedCountries],
    );
  }

  return { imported, dropped, sourceFileName };
};
